import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react";
import { BACKEND_URL } from "../config/api";

const fmtMoney = (n, c = "USD") =>
  `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const DOT = {
  completed: "bg-emerald-500",
  created: "bg-gray-300",
  pending: "bg-amber-400",
  requires_payment_instrument: "bg-gray-300",
  requires_user_action: "bg-amber-400",
  failed: "bg-red-400",
  canceled: "bg-gray-300",
};
const STATUS_TEXT = { requires_payment_instrument: "awaiting payment", requires_user_action: "awaiting action", created: "checkout created" };
const TYPE_LABEL = {
  deposit: "Deposit", full: "Payment", balance: "Balance", extras: "Extras",
  balance_collection: "Balance", customer_ticket_payment: "Balance", balance_payment: "Balance",
  customer_booking: "Payment", gift_card_purchase: "Gift card", marketing_credits: "Ad credits",
  reconciled_charge: "Card charge", refund: "Refund", payout: "Payout",
};

const PROVIDERS = [["all", "All processors"], ["stripe", "Stripe"], ["ziina", "Ziina"]];
const TYPES = [["", "All types"], ["payment", "Payments"], ["refund", "Refunds"], ["payout", "Payouts"]];
const STATUSES = [["", "All statuses"], ["completed", "Completed"], ["created", "Checkout created"], ["requires_payment_instrument", "Awaiting payment"], ["failed", "Failed"]];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("jwtToken")}` });

const SuperAdminFinancialsTab = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("all");
  const [txType, setTxType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const fetchRows = useCallback(async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset: nextOffset };
      if (provider !== "all") params.provider = provider;
      if (txType) params.type = txType;
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${BACKEND_URL}/api/v2/super-admin/financial-transactions`, {
        headers: authHeaders(), params,
      });
      setRows((prev) => (append ? [...prev, ...(r.data.transactions || [])] : r.data.transactions || []));
      setSummary(r.data.summary || null);
      setTotal(r.data.total || 0);
      setOffset(nextOffset);
    } catch { /* table stays empty */ }
    setLoading(false);
  }, [provider, txType, status, search]);

  useEffect(() => { fetchRows(0); }, [provider, txType, status]); // eslint-disable-line

  const volumeLine = (p) => {
    const by = summary?.[p]?.completedByCurrency || {};
    const parts = Object.entries(by).map(([c, v]) => fmtMoney(v.volume, c));
    return parts.length ? parts.join(" + ") : "—";
  };

  const selectCls = "h-9 px-3 text-[13px] border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:border-gray-400 transition-colors";

  return (
    <div className="space-y-4" data-testid="superadmin-financials-tab">
      <div>
        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Financials</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Platform-wide financial audit log — every checkout, payment, refund and payout across Stripe and Ziina, with the full metadata record.
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 border border-gray-200 rounded-lg divide-y sm:divide-y-0 divide-x-0 sm:divide-x divide-gray-200 bg-white" data-testid="financials-summary">
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Records</p>
          <p className="text-sm text-gray-900 tabular-nums mt-0.5">{total.toLocaleString()}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Stripe collected</p>
          <p className="text-sm text-gray-900 tabular-nums mt-0.5">{volumeLine("stripe")}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Ziina collected</p>
          <p className="text-sm text-gray-900 tabular-nums mt-0.5">{volumeLine("ziina")}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Refunded</p>
          <p className="text-sm text-gray-900 tabular-nums mt-0.5">
            {summary ? ((summary.stripe?.refunded || 0) + (summary.ziina?.refunded || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "—"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="financials-provider-filter" value={provider} onChange={(e) => setProvider(e.target.value)} className={selectCls}>
          {PROVIDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="financials-type-filter" value={txType} onChange={(e) => setTxType(e.target.value)} className={selectCls}>
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="financials-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-3.5 h-3.5 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="financials-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchRows(0)}
            placeholder="Code, email, name or intent id…"
            className="w-full h-9 pl-8 pr-3 text-[13px] border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-gray-400 transition-colors"
          />
        </div>
        <button data-testid="financials-refresh-btn" onClick={() => fetchRows(0)} className="h-9 px-3 border border-gray-200 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" aria-label="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Ledger */}
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-testid="financials-table">
        {rows.length === 0 && !loading ? (
          <p className="text-xs text-gray-400 py-10 text-center">No transactions match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="text-left border-b border-gray-200">
                  {["", "Date", "Processor", "Organization", "Booking", "Customer", "Type", "Amount", "Status"].map((h, i) => (
                    <th key={i} className={`py-2.5 px-3 text-[11px] uppercase tracking-wider text-gray-400 font-normal ${h === "Amount" ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((tx) => (
                  <>
                    <tr
                      key={tx.id}
                      data-testid={`financials-row-${tx.id}`}
                      onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}
                      className="border-b border-gray-100 cursor-pointer hover:bg-gray-50/60 transition-colors"
                    >
                      <td className="py-2.5 pl-3 w-6 text-gray-300">
                        {expanded === tx.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-500 whitespace-nowrap">{fmtDate(tx.createdAt)}</td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-900 capitalize">{tx.provider}</td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-600 max-w-[160px] truncate">{tx.orgName || tx.orgSlug || "—"}</td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-900 whitespace-nowrap">{tx.confirmationCode || "—"}</td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-600 max-w-[150px] truncate">{tx.customerName || tx.customerEmail || "—"}</td>
                      <td className="py-2.5 px-3 text-[13px] text-gray-500">
                        {tx.type === "refund" ? "Refund" : tx.type === "payout" ? "Payout" : (TYPE_LABEL[tx.paymentType] || "Payment")}
                      </td>
                      <td className={`py-2.5 px-3 text-[13px] text-right tabular-nums whitespace-nowrap ${tx.type === "refund" ? "text-red-600" : "text-gray-900"}`}>
                        {tx.type === "refund" ? "−" : ""}{fmtMoney(tx.amount, tx.currency)}
                        {tx.test && <span className="ml-1.5 text-[10px] text-gray-300">test</span>}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="flex items-center gap-1.5 text-[13px] text-gray-600 whitespace-nowrap">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[tx.status] || "bg-gray-300"}`} />
                          {STATUS_TEXT[tx.status] || (tx.status || "").replace(/_/g, " ")}
                        </span>
                      </td>
                    </tr>
                    {expanded === tx.id && (
                      <tr key={`${tx.id}-meta`} className="border-b border-gray-100 bg-gray-50/40">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1 max-w-4xl" data-testid={`financials-meta-${tx.id}`}>
                            {[
                              ["Source", tx.source],
                              ["Booking id", tx.bookingId],
                              ["Platform fee", tx.platformFee ? fmtMoney(tx.platformFee, tx.currency) : null],
                              ["Processor fee", tx.processorFee ? fmtMoney(tx.processorFee, tx.currency) : null],
                              ["Payout status", tx.payoutStatus],
                              ["Description", tx.description],
                              ...Object.entries(tx.externalIds || {}).filter(([, v]) => v).map(([k, v]) => [k, v]),
                            ].filter(([, v]) => v).map(([k, v]) => (
                              <div key={k} className="flex gap-2 text-[12px] py-0.5">
                                <span className="text-gray-400 shrink-0 w-28 capitalize">{k}</span>
                                <span className="text-gray-700 break-all">{String(v)}</span>
                              </div>
                            ))}
                            {Object.keys(tx.meta || {}).length > 0 && (
                              <div className="md:col-span-2 mt-2 pt-2 border-t border-gray-200">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Metadata record</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
                                  {Object.entries(tx.meta).map(([k, v]) => (
                                    <div key={k} className="flex gap-2 text-[12px] py-0.5">
                                      <span className="text-gray-400 shrink-0 w-40 truncate">{k}</span>
                                      <span className="text-gray-700 break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows.length < total && (
          <div className="py-3 text-center border-t border-gray-100">
            <button
              data-testid="financials-load-more-btn"
              onClick={() => fetchRows(offset + LIMIT, true)}
              disabled={loading}
              className="text-[13px] text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors disabled:opacity-50"
            >
              {loading ? "Loading…" : `Load more (${rows.length} of ${total.toLocaleString()})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminFinancialsTab;
